import React from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { FDImage, FDLabelText, FDStack } from '@facet-wealth/design';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMobileLayout from 'Hooks/useMobileLayout';
import { impactOverrides } from './overrides';
import { ImpactPaper, MOBILE_PAPER_SPACING, DESKTOP_PAPER_SPACING } from './paper';
import { QuoteIcon } from './quoteIcon';

const DESKTOP_IMAGE_SIZE = 60;
const MOBILE_IMAGE_SIZE = 48;
const IMAGE_SPACING = 24;
const DESKTOP_PADDING_TO_PREVENT_IMAGE_OVERLAPPING_TEXT = Math.max(DESKTOP_IMAGE_SIZE - DESKTOP_PAPER_SPACING - IMAGE_SPACING, 0);
const MOBILE_PADDING_TO_PREVENT_IMAGE_OVERLAPPING_TEXT = Math.max(MOBILE_IMAGE_SIZE - MOBILE_PAPER_SPACING - IMAGE_SPACING, 0);

const useStyles = buildUseStyles('QuoteImpact', (theme) => ({
  root: {
    position: 'relative',
    flex: 1,
    display: 'flex',
    marginBottom: theme.spacing(1),
  },
  image: {
    position: 'absolute',
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    bottom: -IMAGE_SPACING,
    right: -IMAGE_SPACING,
    // Be on top of the content
    zIndex: 1,

    '& > img': {
      width: ({ isMobile }) => (isMobile ? MOBILE_IMAGE_SIZE : DESKTOP_IMAGE_SIZE),
      height: ({ isMobile }) => (isMobile ? MOBILE_IMAGE_SIZE : DESKTOP_IMAGE_SIZE),
    },
  },
  quoteAttribution: {
    paddingRight: ({ isMobile }) => (isMobile ? MOBILE_PADDING_TO_PREVENT_IMAGE_OVERLAPPING_TEXT : DESKTOP_PADDING_TO_PREVENT_IMAGE_OVERLAPPING_TEXT),
  },
  quoteIcon: {
    position: 'absolute',
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-flex-start',
    top: theme.spacing(1),
    left: theme.spacing(2),
  },
}));

export function QuoteImpact({ resource }) {
  const isMobile = useMobileLayout();
  const classes = useStyles({ isMobile });
  return (
    <div className={classes.root}>
      {resource.withRelationship(
        fr.relationships.image.name,
        (rel) => (
          <div className={classes.image}>
            <FDImage src={rel.link('related').url()} alt={rel.meta('prompt') ?? resource.attr('value')} />
          </div>
        ),
        () => null,
      )}
      <div className={classes.quoteIcon}>
        <QuoteIcon />
      </div>
      <ImpactPaper>
        <FDStack direction="column" justifyContent="flex-start" alignItems="flex-start" height="100%">
          <MetaControl resource={resource} metaOverrideSet={impactOverrides} attrName="headline" />
          <MetaControl resource={resource} metaOverrideSet={impactOverrides} attrName="description" />
          <FDLabelText
            value={(
              <div className={classes.quoteAttribution}>
                {resource.attr('value')}
              </div>
            )}
            size="md"
            align="start"
          />
        </FDStack>
      </ImpactPaper>
    </div>
  );
}

QuoteImpact.propTypes = {
  resource: PropTypes.object.isRequired,
};
